<template>
  <v-card id="concept-detail-container" data-testid="concept-detail-card">
    <v-container class="d-flex flex-column concept-attributes-display" data-testid="concept-attributes-container">

      <v-card-text class="my-3 pa-0" data-testid="concept-detail-header-text">
        <v-row id="detail-head" class="align-center" data-testid="detail-head-row">
          <v-chip v-if="store.conceptDetail.class" color="primary" dark class="ma-0 px-4" data-testid="concept-type-chip-class">
            {{ translationConcept[$route.query.lang] || translationConcept["en"] }}
          </v-chip>
          <template v-else>
            <v-chip color="#531852" dark class="ma-0 px-4" data-testid="concept-type-chip-individual">
              {{ translationIndividual[$route.query.lang] || translationIndividual["en"] }}
            </v-chip>
          </template>
          <AttributesToClipBtn :attributeValues="dataForClipBoardBtn.attributeValues"
            :attributesNames="dataForClipBoardBtn.attributesNames" data-testid="copy-to-clipboard-btn" />
        </v-row>
      </v-card-text>


      <!-- Affichage de l'id du concept directement-->
      <AttributeDisplay label="ID" title="ID" :values="[store.conceptDetail.id]" data-testid="concept-id-display" />

      <!-- Affichage des attributs sous forme de sections ou classique -->
      <template v-if="nbrAttrSec > 0 && sections.length > 0">
        <!-- Sections configurées -->
        <Section v-for="(section, index) in sections" :key="index" :section="section" :lang="$route.query.lang"
          :attributesFormatted="attributesFormattedBySection[section.name]" :data-testid="'section-' + section.name" />
        <!-- Section "autres"-->
        <Section v-if="displayMode !== 'mappingsView'"
          :section="{ name: 'Other informations', labels: [{ lang: 'fr', value: 'Autres informations' }], displayType: 'accordion-closed' }"
          :lang="$route.query.lang" :attributesFormatted="attributesFormattedOtherSection" data-testid="section-other-informations" />
      </template>
      <AttributesDisplay v-else :conceptDetailFormatted="conceptDetailFormatted"
        :attributesFormatted="attributesFormattedLegacy" data-testid="legacy-attributes-display" />

    </v-container>
  </v-card>
</template>

<script>
import ConceptsStore from "../concepts-store";
import StoreConfiguration from "../store-configuration";
import TRANSLATIONS from "../enums/translations";
import BASIC_ATTRIBUTES from "../enums/basic_attributes";
import OTHER_ATTRIBUTES from "../enums/other_attributes";
import PREFIX_REFERENCE from "../enums/prefix_reference";
import Utils from "../utils";
import AttributesDisplay from "./detail/AttributesDisplay";
import AttributesToClipBtn from "./detail/AttributesToClipBtn";
import Section from "./detail/Section";

const REGEX_URL = new RegExp('^(https?:\\/\\/)', 'i');
const PARSER = new DOMParser();

export default {
  name: "ConceptDetail",

  components: {
    AttributesDisplay,
    AttributesToClipBtn,
    Section
  },

  props: {
    displayMode: {
      type: String,
      default: "detailView"
    },
    conceptIdValue: {
      lang: "und",
      content: "ID"
    },
  },

  data() {
    return {
      store: ConceptsStore.state,
      storeConfiguration: StoreConfiguration.state,
      conceptDetailFormatted: {},
      attributesFormatted: [],
      basicAttributes: {
        "array": Object.values(BASIC_ATTRIBUTES),
        "object": BASIC_ATTRIBUTES,
      },
      translationConcept: TRANSLATIONS.concept,
      translationIndividual: TRANSLATIONS.individual,
      translationBoolean: TRANSLATIONS.boolean,
      structure_libs_name: null,
      structure_libs: [],
      sections: [],
      nbrAttrSec: 0,
      attributesFormattedBySection: {},
      attributesFormattedOtherSection: [],
      attributesFormattedLegacy: [],
      nbAxiomesAnonymes: 0,
      nbAxiomesLogiques: 0,
      selectedLang: ""
    }
  },

  created() {
    this.formatConceptDetail();
    this.updateBrowserTabTitle();
    this.updateAttributes();
    this.extractNbAxiomes();
  },
  mounted() {
    this.updateSelectedLang(); // Récupérer la langue actuelle au chargement

    const langContainer = this.$el.querySelector('.global-language-selector-container');
    if (langContainer) {
      // Observer les changements dans le sélecteur de langue
      this.observer = new MutationObserver((mutationsList) => {
        for (const mutation of mutationsList) {
          if (mutation.type === 'childList' || mutation.type === 'attributes') {
            this.updateSelectedLang();
          }
        }
      });

      // Configurer l'observation sur les mutations de classe et d'enfants
      this.observer.observe(langContainer, {
        attributes: true, // Surveiller les changements de classe
        subtree: true, // Observer aussi les enfants de `langContainer`
        attributeFilter: ['class'], // Se concentrer uniquement sur la classe
        childList: true // Observer les ajouts/suppressions d'éléments
      });
    }
  },

  beforeDestroy() {
    if (this.observer) {
      this.observer.disconnect();
    }
  },

  updated() {
    this.updateBrowserTabTitle();
  },


  watch: {
    '$route.query.lang': {
      handler: function () {
        this.formatConceptDetail();
        this.updateAttributes();
      },
      deep: true,
      immediate: true
    },
    displayMode() {
      this.formatConceptDetail();
      this.updateAttributes();
    }
  },

  computed: {
    //values to be copied to the clipboard
    dataForClipBoardBtn: function () {
      const div = document.createElement('div');
      const values = this.getMultilingualValues(this.conceptDetailFormatted[BASIC_ATTRIBUTES.label], true);
      const matchingValue = values.length > 0
        ? values.find(v => v.lang === this.selectedLang) || values[0]
        : null;

      div.innerHTML = matchingValue ? matchingValue.content : '';

      let labelValuePOurClipboard;
      // Ne pas valoriser le label pour le presse-papier si la traduction dans la langue choisie est absente
      if (matchingValue == null || matchingValue == undefined) {
        labelValuePOurClipboard = " ";
      } else {
        labelValuePOurClipboard = div.firstChild?.innerText || div.firstChild?.textContent || '';
      }
      let notation = "";
      // Gérer les différents cas de figure en fonction du modèle API (avant ou après R11)
      if (this.conceptDetailFormatted[BASIC_ATTRIBUTES.notation] != undefined) {
        if (this.conceptDetailFormatted[BASIC_ATTRIBUTES.notation]["und"]["value"] != null
          || this.conceptDetailFormatted[BASIC_ATTRIBUTES.notation]["und"]["value"] != undefined) {
          notation = this.conceptDetailFormatted[BASIC_ATTRIBUTES.notation]["und"]["value"][0];
        } else if (this.conceptDetailFormatted[BASIC_ATTRIBUTES.notation]["und"]["literals"] != null) {
          notation = this.conceptDetailFormatted[BASIC_ATTRIBUTES.notation]["und"]["literals"][0]["value"];
        }
      }

      return {
        attributeValues: {
          uri: this.store.conceptDetail.id,
          label: labelValuePOurClipboard,
          notation,
        },
        attributesNames: {
          uri: "ID",
          label: this.getLabelTranslation('label'),
          notation: this.getLabelTranslation('notation'),
        }
      }
    },
  },

  methods: {
    updateSelectedLang() {
      const selectedLangElement = this.$el.querySelector('.global-language-selector-selected span.global-language-selector');
      if (selectedLangElement) {
        let langText = selectedLangElement.textContent.trim().toLowerCase();
        if (langText === "") {
          langText = "und";
        }
        this.selectedLang = langText;
      }
    },

    extractLangValues(obj, values = []) {
      for (const key in obj) {
        if (typeof obj[key] === 'object' && obj[key] !== null) {
          this.extractLangValues(obj[key], values);
        } else if (key === 'lang') {
          values.push(obj[key].toUpperCase());
        }
      }
      return values;
    },

    /**
     * Calcule le nombre d'axiomnes logiques et anonymes
     */
    extractNbAxiomes() {
      let nbAxiomesAnonymes = 0;
      let nbAxiomesLogiques = 0;

      const compterBlankNodes = (attributes) => {
        attributes.forEach(attribute => {
          if (attribute.blankNodes) {
            attribute.blankNodes.forEach(blankNode => {
              if (blankNode.blankNodeType === 'LOGICAL_AXIOM') {
                nbAxiomesLogiques++;
              } else {
                nbAxiomesAnonymes++;
              }
            });
          }
        });
      };

      compterBlankNodes(this.attributesFormattedLegacy);
      compterBlankNodes(this.attributesFormattedOtherSection);

      Object.values(this.attributesFormattedBySection).forEach(section => {
        compterBlankNodes(section);
      });

      this.$parent.$refs.filtresAxiomes.updateInformation(nbAxiomesAnonymes, nbAxiomesLogiques);
    },

    updateAttributes() {
      this.sections = this.getSections();

      this.nbrAttrSec = 0;
      if (this.sections.length > 0) {
        this.sections.forEach((section) => {
          let attributeForOneSection = this.getAttributesFormattedBySection(section);
          this.nbrAttrSec += attributeForOneSection.length;
          this.attributesFormattedBySection[section.name] = attributeForOneSection;
        });
        this.attributesFormattedOtherSection = this.getAttributesFormattedOtherSection();
      }
      // Pas de section ou aucun attribut dans les sections
      if (this.nbrAttrSec <= 0) {
        this.attributesFormattedBySection = {};
        this.attributesFormattedOtherSection = [];
        this.attributesFormattedLegacy = this.getAttributesFormattedLegacy();
      }
    },

    getAttributesFormattedLegacy() {
      let filteredAttributes = this.getFilteredAttributes();

      const valeursMasquees = this.storeConfiguration?.current?.concepts_details?.affichage?.valeurs_masquees || {};
      return filteredAttributes.filter(attribute => {
        // 🔍 Vérifier si `attribute.uri` ou l'une de ces axiomes contient des attributs à masquées`
        const shouldFilter = attribute.uri in valeursMasquees || this.containsMaskedAxiom(attribute, valeursMasquees);

        if (shouldFilter) {
          // Filtrer les littéraux
          if (attribute.litterals.length > 0) {
            attribute.litterals = this.filterLiterals(attribute.litterals, valeursMasquees[attribute.uri]?.list || []);
          }

          // Filtrer les valeurs
          if (attribute.values?.length > 0) {
            attribute.values = this.filterValues(attribute.values, valeursMasquees[attribute.uri]?.list || []);
          }

          // Filtrer les annotations d'axiomes
          if (attribute.litterals) {
            attribute.litterals.forEach(literal => {
              if (literal.axiomAnnotations?.length > 0) {
                literal.axiomAnnotations = this.filterAxiomAnnotations(literal.axiomAnnotations, valeursMasquees);
              }
            });
          }
        }

        // Vérification finale : masquer complètement l'attribut si tout est vide
        return this.shouldKeepAttribute(attribute);
      });
    },

    getFilteredAttributes() {
      return !this.isDetailView()
        ? this.attributesFormatted.filter(attribute =>
          attribute.visible &&
          (attribute.uri === BASIC_ATTRIBUTES.label ||
            attribute.uri === BASIC_ATTRIBUTES.altLabel ||
            attribute.uri === BASIC_ATTRIBUTES.notation)
        )
        : this.attributesFormatted.filter(attribute => attribute.visible);
    },

    // **Fonction pour filtrer les littéraux**
    filterLiterals(literals, list) {
      return literals.filter(literal => {
        const extractedText = literal.content.replace(/<a[^>]*>(.*?)<\/a>/i, '$1');
        return !list.some(maskedObj => {
          const { value: maskedValue, mode } = maskedObj;
          return mode === "exact"
            ? extractedText === maskedValue
            : new RegExp(maskedValue, "i").test(extractedText);
        });
      });
    },

    // **Fonction pour filtrer les valeurs**
    filterValues(values, list) {
      return values.filter(value => {
        const extractedText = value.replace(/<a[^>]*>(.*?)<\/a>/i, '$1');
        return !list.some(maskedObj => {
          const { value: maskedValue, mode } = maskedObj;
          return mode === "exact"
            ? extractedText === maskedValue
            : new RegExp(maskedValue, "i").test(extractedText);
        });
      });
    },

    // Filtrer les annotations d'axiomes (avec logs pour voir où ça bloque)
    filterAxiomAnnotations(axiomAnnotations, valeursMasquees) {
      return axiomAnnotations.map(axiom => {
        if (axiom.other) {
          axiom.other = axiom.other.filter(other => {
            const keyMasked = valeursMasquees[other.key];
            if (keyMasked) {
              const list = keyMasked.list;
              if (other.literals) {
                other.literals = this.filterLiterals(other.literals, list);
              }
              return other.literals.length > 0; // Garder `other` **seulement** s'il reste des valeurs après filtrage
            }
            return true; // Si `other.key` n'est pas dans `valeursMasquees`, on le garde
          });
        }
        return axiom.other.length > 0 ? axiom : null;  // Supprimer `axiomAnnotation` si toutes les `other` ont été supprimées
      }).filter(Boolean); // Supprimer les `axiomAnnotation` vides
    },

    // Vérifie si un `axiomAnnotation` contient un élément à masqué
    containsMaskedAxiom(attribute, valeursMasquees) {
      if (!attribute.litterals) return false;

      return attribute.litterals.some(literal =>
        literal.axiomAnnotations?.some(axiom =>
          axiom.other?.some(other => other.key in valeursMasquees)
        )
      );
    },

    // **Vérification finale pour supprimer un attribut complètement vide**
    shouldKeepAttribute(attribute) {
      const hasLiterals = attribute.litterals && attribute.litterals.length > 0;
      const hasValues = attribute.values && attribute.values.length > 0;
      const hasBlankNodes = attribute.blankNodes && attribute.blankNodes.length > 0;

      if (!hasLiterals && !hasValues && !hasBlankNodes) {
        return false;
      }
      return true;
    },

    getAttributesFormattedBySection: function (section) {
      // Deep Copy as we need to customise attributes order for each section.
      let sectionAttributes = JSON.parse(JSON.stringify(this.attributesFormatted.filter(attribute => {
        if (Array.isArray(this.storeConfiguration.current?.concepts_details?.attributs?.ordre)) {
          return attribute.visible
            && this.storeConfiguration.current?.concepts_details?.attributs.ordre.filter(attrConfig => attrConfig.section === section.name && (BASIC_ATTRIBUTES[attrConfig.uri] ? BASIC_ATTRIBUTES[attrConfig.uri] : attrConfig.uri) === attribute.uri).length > 0;
        } else {
          return false;
        }
      })));
      sectionAttributes.forEach(attribute => {
        attribute.order = this.getAttributeOrder(attribute.uri, section.name);
      });
      return sectionAttributes;
    },

    getAttributesFormattedOtherSection: function () {
      let excludes = this.getOtherSectionExcludeAttributes();
      return this.attributesFormatted.filter(attribute => attribute.visible && excludes.indexOf(attribute.uri) < 0);
    },

    //tell if the attribute should be displayed (eg: we don't want to display some concept's properties on the mappings tab)
    isDetailView: function () {
      return this.displayMode === "detailView";
    },

    getLabelTranslation(label) {
      if (this.storeConfiguration.current?.concepts_details?.attributs?.label_affichage) {

        let basicUri = BASIC_ATTRIBUTES[label] ? BASIC_ATTRIBUTES[label] : ' ';

        const translations = this.storeConfiguration.current?.concepts_details?.attributs?.label_affichage[label]
          ? this.storeConfiguration.current?.concepts_details?.attributs?.label_affichage[label]
          : this.storeConfiguration.current?.concepts_details?.attributs?.label_affichage[basicUri]
            ? this.storeConfiguration.current?.concepts_details?.attributs?.label_affichage[basicUri]
            : [];

        if (translations.length && translations.find(x => x.lang === this.$route.query.lang)) {
          return translations.filter(x => x.lang === this.$route.query.lang)[0].name;
        }
      }

      // No label is available for the label so take the last part of the URI.
      const regexMatch = label.match('([^\\/|#|:]+$)');
      return regexMatch === null ? label : regexMatch[1];
    },

    /**
     * Format concept detail to simplify the retrieving of data.
     */
    formatConceptDetail: function () {

      this.attributesFormatted.length = 0;

      this.store.conceptDetail.other.forEach((item) => {
        this.conceptDetailFormatted[item.key] = {
          ...this.conceptDetailFormatted[item.key],
          ...this.getLangData(item)
        };
      });

      this.structure_libs_name = this.storeConfiguration.current?.concepts_details?.affichage?.structure_libs?.map(attribut => attribut.name);
      this.structure_libs = this.storeConfiguration.current?.concepts_details?.affichage?.structure_libs;
      let label = this.conceptDetailFormatted[BASIC_ATTRIBUTES.label];



      if (label) {
        this.attributesFormatted.push({
          uri: BASIC_ATTRIBUTES.label,
          title: 'rdfs:label',
          label: this.getLabelTranslation(BASIC_ATTRIBUTES.label),
          litterals: this.getLiterals(BASIC_ATTRIBUTES.label, label),
          axiomAnnotations: this.getBlankNodes(BASIC_ATTRIBUTES.axiomAnnotations),
          blankNodes: this.getBlankNodesFromMap(label),
          accordion: this.checkAccordion('label'),
          accordiontDisplayOpen: this.checkAccordionDisplayOpen('label'),
          display: 'property',
          order: this.getAttributeOrder('label'),
          visible: this.getAttributeVisibility('label'),
          negative: this.getNegative(label)
        });
      }

      let altLabel = this.conceptDetailFormatted[BASIC_ATTRIBUTES.altLabel];
      if (altLabel) {
        this.attributesFormatted.push({
          uri: BASIC_ATTRIBUTES.altLabel,
          title: 'skos:altLabel',
          label: this.getLabelTranslation(BASIC_ATTRIBUTES.altLabel),
          litterals: this.getLiterals(BASIC_ATTRIBUTES.altLabel, altLabel),
          blankNodes: this.getBlankNodesFromMap(altLabel),
          accordion: this.checkAccordion('altLabel'),
          accordiontDisplayOpen: this.checkAccordionDisplayOpen('altLabel'),
          order: this.getAttributeOrder('altLabel'),
          visible: this.getAttributeVisibility('altLabel'),
          negative: this.getNegative(altLabel)
        });
      }

      let comment = this.conceptDetailFormatted[BASIC_ATTRIBUTES.comment];
      if (comment) {
        this.attributesFormatted.push({
          uri: BASIC_ATTRIBUTES.comment,
          title: 'rdfs:comment',
          label: this.getLabelTranslation(BASIC_ATTRIBUTES.comment),
          litterals: this.getLiterals(BASIC_ATTRIBUTES.comment, comment),
          blankNodes: this.getBlankNodesFromMap(comment),
          accordion: this.checkAccordion('comment'),
          accordiontDisplayOpen: this.checkAccordionDisplayOpen('comment'),
          order: this.getAttributeOrder('comment'),
          visible: this.getAttributeVisibility('comment'),
          negative: this.getNegative(comment)
        });
      }

      let definition = this.conceptDetailFormatted[BASIC_ATTRIBUTES.definition];
      if (definition) {
        this.attributesFormatted.push({
          uri: BASIC_ATTRIBUTES.definition,
          title: 'skos:definition',
          label: this.getLabelTranslation(BASIC_ATTRIBUTES.definition),
          litterals: this.getLiterals(BASIC_ATTRIBUTES.definition, definition),
          blankNodes: this.getBlankNodesFromMap(definition),
          accordion: this.checkAccordion('definition'),
          accordiontDisplayOpen: this.checkAccordionDisplayOpen('definition'),
          order: this.getAttributeOrder('definition'),
          visible: this.getAttributeVisibility('definition'),
          negative: definition?.negative ? definition.negative : false
        });
      }

      let notation = this.conceptDetailFormatted[BASIC_ATTRIBUTES.notation];
      if (notation) {
        this.attributesFormatted.push({
          uri: BASIC_ATTRIBUTES.notation,
          title: 'skos:notation',
          label: this.getLabelTranslation(BASIC_ATTRIBUTES.notation),
          litterals: this.getLiterals(BASIC_ATTRIBUTES.notation, notation),
          blankNodes: this.getBlankNodesFromMap(notation),
          accordion: this.checkAccordion('notation'),
          accordiontDisplayOpen: this.checkAccordionDisplayOpen('notation'),
          order: this.getAttributeOrder('notation'),
          visible: this.getAttributeVisibility('notation'),
          negative: notation?.negative ? notation.negative : false
        });
      }


      Object.keys(this.conceptDetailFormatted).forEach((key) => {
        let item = this.conceptDetailFormatted[key];
        if (!Object.values(BASIC_ATTRIBUTES).includes(key)) {
          const hasSpecialKey = ['null', 'und'].some(k => Object.hasOwn(item, k));
          let currentLiteral = this.getLiterals(key, item);
          let currentOtherValue = (Object.keys(item).length === 1 && hasSpecialKey)
            ? this.getOtherValue(item[Object.keys(item)[0]], '', key, '', true, 'none')
            : undefined;

          // Extract the negative value from the object
          let negative = item[Object.keys(item)[0]]?.negative ? true : false;

          if (currentOtherValue) {
            currentLiteral.push(...currentOtherValue);
          }

          let attributeFormatted = {
            uri: key,
            title: this.getTitle(key),
            label: this.getLabelTranslation(key),
            litterals: currentLiteral,
            blankNodes: this.getBlankNodesFromMap(item),
            accordion: this.checkAccordion(key),
            accordiontDisplayOpen: this.checkAccordionDisplayOpen(key),
            display: (Object.keys(item).length === 1 && hasSpecialKey) ? 'text' : undefined,
            order: this.getAttributeOrder(key),
            visible: this.getAttributeVisibility(key),
            negative: negative
          };

          this.attributesFormatted.push(attributeFormatted);
        }
      });
    },

    getMultilingualValues: function (item, sortCurrentLangFirst = false) {
      let res = this.formatMultilingualValue(item, true);
      if (sortCurrentLangFirst) {
        res = res.sort((x, y) => x.lang === this.$route.query.lang ? -1 : y.lang === this.$route.query.lang ? 1 : 0);
      }
      return res;
    },


    /**
   *
   * @param currentObj : objet à parcourir
   * @param lang : lang de l'objet
   */
    getvalueUris: function (currentObj, lang) {
      let $self = this;
      let valueUris = [];
      let blocNumber = 0;
      if (currentObj.valueUris) {
        for (let uri in currentObj.valueUris) {
          blocNumber++;
          valueUris.push(...
            StoreConfiguration.formatValueUri(currentObj.valueUris[uri], this.store.terminologyId, 'none', lang, $self.getBlankNodes(currentObj.valueUris[uri].axiomAnnotations), blocNumber)
          );
        }
      }


      return valueUris;
    },


    /**
   *
   * @param currentObj : objet à parcourir
   */
    getLiterals: function (uri, currentObj) {
      let $self = this;
      let literals = [];
      Object.keys(currentObj).forEach(lang => {
        let item = currentObj[lang];
        if (item.literals && item.literals.length > 0) {
          item.literals.forEach(function (literal) {
            literals.push({
              lang: lang === "null" ? "" : lang,
              content: StoreConfiguration.formatValueLiteralsFromContent(literal.value),
              axiomAnnotations: $self.getBlankNodes(literal.axiomAnnotations)
            });
          });
        }
      });



      // Tri des literaux en fonction de la configuration.
      StoreConfiguration.sortAttributeValueByLiteral(literals, uri, this.storeConfiguration.current);
      return literals;
    },


    /**
   *
   * @param currentObj : retourne negative si true
   */
    getNegative: function (currentObj) {
      let negative = false;
      Object.keys(currentObj).forEach(lang => {
        let item = currentObj[lang];
        if (item.negative === true) {
          negative = true;
        }
      });
      return negative;
    },

    /**
    *
    * @param currentObj : objet à parcourir
    * @param lang : lang de l'objet
    */
    getLiteral: function (currentObj, lang, uri, axiomeLogique) {
      let literals = [];
      if (currentObj.literals) {
        currentObj.literals.forEach(function (literal) {
          let content;
          if (axiomeLogique) {
            content = StoreConfiguration.extractUriLastPart(literal.value);
          } else {
            content = StoreConfiguration.formatValueLiteralsFromContent(literal.value);
          }
          literals.push({
            lang: lang || "",
            content: content,
            axiomAnnotations: literal.axiomAnnotations
          });
        });
      }

      StoreConfiguration.sortAttributeValueByLiteral(literals, uri, this.storeConfiguration.current);
      return literals;
    },

    getLangData: function (item) {
      let langData = [];
      langData[(item.lang) ? item.lang : ((item.lang === null) ? null : "und")] = { ...item };
      return langData;
    },

    findOtherByUri: function (other, uri) {
      return other.find(e => {
        return e.key && e.key === uri;
      });
    },

    getAttributeOrder(attribute, section) {
      if (
        this.storeConfiguration.current?.concepts_details?.attributs?.ordre
        && this.storeConfiguration.current.concepts_details.attributs.ordre.length
      ) {
        let basicUri = BASIC_ATTRIBUTES[attribute] ? BASIC_ATTRIBUTES[attribute] : ' ';

        let attributsOrdered = this.storeConfiguration.current?.concepts_details?.attributs?.ordre;
        if (section && section.length > 0) {
          attributsOrdered = attributsOrdered.filter(attribut => attribut.section === section);
        }

        let attributsUriOrdered = attributsOrdered?.map(attribut => BASIC_ATTRIBUTES[attribut.uri] ? BASIC_ATTRIBUTES[attribut.uri] : attribut.uri);
        if (attributsUriOrdered.indexOf(attribute) > -1) {
          return attributsUriOrdered.indexOf(attribute)
        } else if (attributsUriOrdered.indexOf(basicUri) > -1) {
          return attributsUriOrdered.indexOf(basicUri)
        }

        return (attributsUriOrdered.length + 1)
      }
      return undefined;
    },

    getAttributeVisibility(attribute) {
      if (
        this.storeConfiguration.current?.concepts_details?.attributs?.masques
        && Object.keys(this.storeConfiguration.current.concepts_details.attributs.masques).length
      ) {
        let basicUri = BASIC_ATTRIBUTES[attribute] ? BASIC_ATTRIBUTES[attribute] : ' ';

        if (
          Object.values(this.storeConfiguration.current?.concepts_details?.attributs?.masques).indexOf(attribute) > -1
          || Object.values(this.storeConfiguration.current?.concepts_details?.attributs?.masques).indexOf(basicUri) > -1
        ) {
          return false;
        }
      }
      return true;
    },

    /**
     * Format attribute's value as the right array of objects in case of multilingual display.
     */
    formatMultilingualValue: function (attributeValue) {
      if (!attributeValue) return [];
      let items = [];

      for (const [key, value] of Object.entries(attributeValue)) {
        items.push(
          {
            lang: key,
            content: value.literals[0] ? value.literals[0].value : ""
          }
        );
      }

      // Sort items by taking into account sorting rules define in the configuration.
      let languagesConf = Utils.convertObjectValuesToArray(this.storeConfiguration.current?.concepts_details?.affichage?.ordre_langues);
      items = Utils.sortArrayBySpecificOrder(items, languagesConf, "lang")

      // Move the undefined item to the last position.
      items.sort((x, y) => x.lang === "und" ? 1 : y.lang === "und" ? -1 : 0);

      return items;
    },

    /**
    * get the short name for an attribute
    */
    getShortLongName: function (searchName) {
      let res = [];
      if (searchName != null && searchName != undefined) {
        for (const [shortN, longN] of Object.entries(BASIC_ATTRIBUTES)) {
          if (searchName == longN || searchName == shortN) {
            res = [shortN, longN];
            break;
          }
        }
        if (res == '') {
          res = [searchName, searchName];
        }
      }
      return res;
    },


    /**
     * get blanc nodes from any items
     * @param currentObj 
     */
    getOthers: function (currentObj, axiomeLogique) {
      let $self = this;
      let results = [];

      currentObj.forEach(item => {

        let allLiterals = $self.getLiteral(item, item.lang, item.key, axiomeLogique);
        allLiterals.push(...$self.getvalueUris(item, item.lang));

        results.push({
          key: item.key,
          negative: item.negative ? item.negative : false,
          title: $self.getTitle(item.key),
          literals: allLiterals,
          blankNodes: $self.getBlankNodes(item?.blankNodes, item?.key)
        });
      });
      // Trier les résultats
      if (results.length > 0) {
        StoreConfiguration.sortOthers(results);
      }

      return results;
    },


    /**
     * 
     * @param blankNodes Extract blank nodes from blanknodes Array
     */
    getBlankNodes: function (blankNodes, key) {
      let $self = this;
      let blankNodesListResult = [];
      if (blankNodes == null || blankNodes == undefined) {
        return blankNodesListResult;
      }
      blankNodes.forEach(function (blankNode) {
        blankNodesListResult.push({
          blankNodeType: blankNode.blankNodeType,
          other: $self.getOthers(blankNode?.other, blankNode.blankNodeType == 'LOGICAL_AXIOM'),
          axiomAnnotations: $self.getBlankNodes(blankNode?.axiomAnnotations)
        });
      });

      // tri les blankNodes si besoin
      if (blankNodesListResult.length > 0) {
        try {
          StoreConfiguration.sortBlankNodesParConf(blankNodesListResult, key, this.storeConfiguration.current);
        } catch (error) {
          console.error("Erreur lors du tri des blankNodes : ", error);
        }
      }
      return blankNodesListResult;
    },




    /**
     * get blanc nodes from any items
     * @param currentObj 
     */
    getBlankNodesFromMap: function (currentObj) {
      let $self = this;
      let blankNodesListResult = [];
      if (currentObj == null || currentObj == undefined) {
        return blankNodesListResult;
      }

      try {
        Object.keys(currentObj).forEach(key => {
          let item = currentObj[key];
          blankNodesListResult = blankNodesListResult.concat($self.getBlankNodes(item.blankNodes, item.key));
        });
      } catch (error) {
        // c'est  un garde fou. Rien à faire
      }

      return blankNodesListResult;
    },


    /**
     * Get other attribute value depending on the language.
     */
    getOtherValue: function (attributeValue, basicName, attributeKey, attributeLang, withStructure, casing) {
      let $self = this;
      let whoToUse = 'none';

      if (Object.prototype.hasOwnProperty.call(attributeValue, 'valueUris') && attributeValue.valueUris && attributeValue.valueUris.length > 0) {
        whoToUse = 'valueUri';
      }

      let otherArray = [];
      if (whoToUse != 'none') {
        const routeLang = this.$route.query.lang;
        const sortLang = attributeValue.lang ? attributeValue.lang : routeLang;

        let currentVal = null;
        if (whoToUse == 'valueUri') {
          // Sort by URI should be excuted before formatValueUri()
          StoreConfiguration.sortAttributeValueByUri(attributeValue?.valueUris, attributeValue.key, this.storeConfiguration.current, sortLang);
          currentVal = attributeValue?.valueUris;
        }

        if (currentVal != null && currentVal != undefined) {
          // Chercher avec le nom court et le nom complet.
          let toStructure = -1;
          if (withStructure && (whoToUse == 'valueUri') && this.structure_libs_name != null && this.structure_libs_name != undefined) {
            toStructure = this.structure_libs_name.indexOf(attributeKey);
            if (toStructure < 0) {
              toStructure = this.structure_libs_name.indexOf(basicName)
            }
          }

          // si pas trouvé ou non demandé alors par défaut sinon selon structure
          if (toStructure < 0) {
            let currentArray = [];
            if (whoToUse == 'valueUri') {
              let blocNumber = 0;
              StoreConfiguration.sortAttributeValueByUri(attributeValue?.valueUris, attributeValue.key, this.storeConfiguration.current, sortLang);
              currentVal.forEach(value => {
                blocNumber++;
                currentArray = currentArray.concat(StoreConfiguration.formatValueUri(value, this.store.terminologyId, casing, sortLang, $self.getBlankNodes(value.axiomAnnotations), blocNumber));
              });
            }

            StoreConfiguration.sortAttributeValueByLiteral(currentArray, attributeValue.key, this.storeConfiguration.current, sortLang);
            otherArray = otherArray.concat(currentArray);

          } else {
            let currentStruct = this.structure_libs[toStructure];
            let currentArray = StoreConfiguration.formatValueWithStructure(currentVal, currentStruct, this.store.terminologyId, sortLang);

            StoreConfiguration.sortAttributeValueByLiteral(currentArray, attributeValue.key, this.storeConfiguration.current);
            otherArray = otherArray.concat(currentArray);
          }
        }
      }

      return otherArray;
    },

    /**
     * Format value depending on rules.
     */
    formatValue: function (value, key, casing) {
      let res = value;
      // Translate the boolean value
      res = this.translateBoolean(res);

      if (!REGEX_URL.test(res)) {
        if (res != undefined && res != null) {
          res = StoreConfiguration.getTextWithCase(res, casing);
        }
        res = this.formatStringHtmlOrPlainText(res);
      } else {
        res = this.formatStringAsLink(res, key);
      }
      return res;
    },

    translateBoolean(value) {
      if (value === 1) value === 'true';
      if (value === 0) value === 'false';
      if (value === 'true' || value === 'false') {
        return this.translationBoolean[value][this.$route.query.lang] || this.translationBoolean[value]["en"];
      }
      return value;
    },

    findOtherByLang(others, lang) {
      return others.find(e => {
        return lang == '' && (!e.lang || e.lang == '')
          || e.lang === lang;
      });
    },

    /**
     * Format a string depending on its value is under HTML or plain text format.
     */
    formatStringHtmlOrPlainText: function (value) {
      let htmlString = PARSER.parseFromString(value, 'text/html');

      if (htmlString?.children[0]?.children[1]?.childElementCount > 0) {
        return "<div class='d-inline wysiwyg'>" + value + "</div>";
      }

      // In case of plain text we want to escape "<" or ">".
      value = Utils.escapeHtml(value);
      return "<div class='d-inline pre-wrap'>" + value + "</div>";
    },

    /**
     * Format a string as a link.
     */
    formatStringAsLink: function (value, key) {
      if (key === OTHER_ATTRIBUTES.broaderTransitive || key === OTHER_ATTRIBUTES.narrowerTransitive)
        return '<a href="/' + this.$searchResultManager.conceptsCatalogSlug + '/' + encodeURIComponent(this.store.terminologyId) + '/?lang=' + this.$route.query.lang + '&concept=' + encodeURIComponent(value) + '&treeViewType=' + this.$route.query.treeViewType + '">' + value + '</a>';

      return '<a href="' + value + '" target="_blank">' + value + '</a>';
    },

    getTitle(label) {
      const regexMatch = label.match('([^\\/|#]+$)');
      const prefixKey = label.replace(/[^\\/|#]+$/gm, '');
      const prefixObject = {};
      Object.keys(PREFIX_REFERENCE).forEach(key => {
        if (!prefixObject[PREFIX_REFERENCE[key]]) {
          prefixObject[PREFIX_REFERENCE[key]] = key;
        }
      });
      return regexMatch === null || !prefixObject[prefixKey] ? label : label.match(/[^\\/|#]+/g).length === 1 ? regexMatch[1] : (prefixObject[prefixKey] + ':' + regexMatch[1]);
    },

    checkAccordion: function (attribute) {
      return this.storeConfiguration.current?.concepts_details?.affichage?.avec_accordeon
        && ((this.basicAttributes.object[attribute] in this.storeConfiguration.current.concepts_details.affichage.avec_accordeon
          && this.storeConfiguration.current.concepts_details.affichage.avec_accordeon[this.basicAttributes.object[attribute]].etat !== 'none')
          || (attribute in this.storeConfiguration.current.concepts_details.affichage.avec_accordeon
            && this.storeConfiguration.current.concepts_details.affichage.avec_accordeon[attribute].etat !== 'none'));
    },


    checkAccordionDisplayOpen: function (attribute) {
      return this.storeConfiguration.current?.concepts_details?.affichage?.avec_accordeon
        && ((this.basicAttributes.object[attribute] in this.storeConfiguration.current.concepts_details.affichage.avec_accordeon
          && this.storeConfiguration.current.concepts_details.affichage.avec_accordeon[this.basicAttributes.object[attribute]].etat === 'open')
          || (attribute in this.storeConfiguration.current.concepts_details.affichage.avec_accordeon
            && this.storeConfiguration.current.concepts_details.affichage.avec_accordeon[attribute].etat === 'open'));
    },

    /**
     * Update browser tab title.
     */
    updateBrowserTabTitle: function () {
      let browserTabTitle;

      let config = StoreConfiguration.getCurrentConceptConfiguration(this.store.conceptDetail.treeClass, this.store.conceptDetail.id);
      if (config.page_title) {
        let props = { ... this.conceptDetailFormatted };
        props['terminologyId'] = this.store.terminologyDetail.terminologyId;
        props['terminologyLabel'] = this.store.terminologyDetail.title;
        props['terminologyVersionInfo'] = this.store.terminologyDetail.versionInfo;
        props['terminologyPublisher'] = this.store.terminologyDetail.publisher;

        if (config.page_title.variables && Object.keys(config.page_title.variables).length > 0) {
          browserTabTitle = StoreConfiguration.getConceptLabelByConfig(this.store.conceptId, config.page_title, props);
        } else {
          browserTabTitle = config.page_title.pattern;
        }
      }

      if (browserTabTitle) {
        document.title = browserTabTitle;
      }
    },

    getSections() {
      if (this.storeConfiguration.current?.concepts_details?.sections && this.storeConfiguration.current?.concepts_details?.sections.length > 0) {
        return this.storeConfiguration.current?.concepts_details?.sections.filter(section => section.tabs.indexOf(this.displayMode == "mappingsView" ? "mappings" : "main") >= 0);
      }
      return [];
    },

    getOtherSectionExcludeAttributes() {
      let excludes = [];
      if (Array.isArray(this.storeConfiguration.current?.concepts_details?.attributs?.ordre)) {
        this.storeConfiguration.current?.concepts_details?.attributs.ordre.forEach(attribut => {
          if (attribut.section && attribut.section.length > 0) {
            // On exclut tous les attributs déjà affectés à une section.
            excludes.push(BASIC_ATTRIBUTES[attribut.uri] ? BASIC_ATTRIBUTES[attribut.uri] : attribut.uri);
          }
        });
      }
      return excludes;
    },
  }
}
</script>

<style lang="scss" scoped>
#concept-detail-container {
  overflow-x: auto;

  ::v-deep .property-value-prefix {
    align-items: center;
    align-content: center;
    text-decoration: line-through;
    color: #1d71b8;
    margin-right: 10px;
  }

  #detail-head {
    border-bottom: unset
  }

  p {
    margin: 0;
  }

  ::v-deep .pre-wrap {
    white-space: pre-wrap;
  }

  ::v-deep .filter-lang-dedicated {
    display: flex;
    flex-direction: column;
  }

  ::v-deep .value-display-container {
    display: flex;
    align-items: center;
  }

  ::v-deep .display-flew-row {
    display: flex;
    flex-direction: row
  }

  ::v-deep .display-flew-column {
    display: flex;
    flex-direction: column;
    height: 100%;
  }

  ::v-deep .display-flew-column-level0 {
    justify-content: center;
  }

  ::v-deep .axiome-logique-operand-level0 {
    align-content: center;
  }

  ::v-deep .lang-container .simple-text-container {
    display: flex;
    align-items: center;
    flex-direction: row;
    justify-content: flex-start;
    margin: 0;
  }

  ::v-deep .attribute-display-container.hasAxiomeLogique.logical-axiome-hidden.hasAxiomeAnonymes.annonymous-axiome-hidden:not(.hasSimpleValues) {
    display: none !important;
  }

  ::v-deep .attribute-display-container:not(.hasAxiomeLogique).hasAxiomeAnonymes.annonymous-axiome-hidden:not(.hasSimpleValues) {
    display: none !important;
  }

  ::v-deep .attribute-display-container.hasAxiomeLogique.logical-axiome-hidden:not(.hasAxiomeAnonymes):not(.hasSimpleValues) {
    display: none !important;
  }

  /* Hide all .blanknode-annonymous-container-level0 inside an .annonymous-axiome-hidden ancestor */
  ::v-deep .annonymous-axiome-hidden .blanknode-annonymous-container-level0 {
    display: none !important;
  }

  /* Hide all .blanknode-logic-container-level0 inside an .logical-axiome-hidden ancestor */
  ::v-deep .logical-axiome-hidden .blanknode-logic-container-level0 {
    display: none !important;
  }

  ::v-deep .axiome-logique-container:has(> .brace-container-left) {
    /* If you want to style the parent container when it has a direct child with the class */
    padding: 5px 0px 5px 0px;
  }

  ::v-deep .brace-container-close {
    border-top: 2px solid #1d71b8;
    border-bottom: 2px solid #1d71b8;
    border-right: 2px solid #1d71b8;
    border-top-right-radius: 7px;
    border-bottom-right-radius: 7px;
    min-width: 10px;
    margin-left: 10px;
    margin-right: 10px;
    align-items: stretch;
  }

  // Add white-space: nowrap for specific classes
  ::v-deep .axiome-logique-container,
  ::v-deep .concept-blanknode-display {
    white-space: nowrap;
  }

  ::v-deep .axiom-annotation-blanknode-container-content {
    position: relative;
  }

  ::v-deep .axiome-logique-item {
    display: flex;
    align-items: center;
  }

  ::v-deep .hidden {
    display: none;
  }

  ::v-deep .normal-font {
    font-style: normal;
  }
}
</style>